import classNames from 'classnames';
import { map, get, chunk, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import { Container } from 'components/Grid';
import { SecondaryHeading } from 'components/Headings';
import { mediumUp, largeUp } from 'constants/mediaQueries';
import Theme from 'styles/theme';
import Device from 'utils/Device';

import CarouselCard from './CarouselCard';

export const SLIDES_PER_VIEWPORT = {
  SMALL: 2.2,
  MEDIUM: 4.2,
  LARGE: 4.2,
};

const CollectionDeadEnd = ({
  filteredSecondareNavigationOptions,
  trackNavigation,
  is3Column,
  exploreMoreTitle,
  currentCollection,
}) => {
  const isMediumUp = Device.isMediumUp;
  const columns = isMediumUp && is3Column ? 3 : 2;

  if (isEmpty(filteredSecondareNavigationOptions)) {
    return null;
  }

  return (
    <div className="CollectionDeadEnd">
      <Container>
        <div
          className={classNames(
            'CollectionDeadEnd__wrapper',
            'CollectionDeadEnd__wrapper-stacked',
            {
              'CollectionDeadEnd__wrapper-two-column': columns === 2,
            }
          )}
        >
          <div className={classNames('CollectionDeadEnd__text', 'CollectionDeadEnd__text-stacked')}>
            <SecondaryHeading>{exploreMoreTitle}</SecondaryHeading>
          </div>
          <div
            className={classNames('CollectionDeadEnd__items', 'CollectionDeadEnd__items-stacked')}
          >
            {map(chunk(filteredSecondareNavigationOptions, columns), (row, index) => (
              <div className="CollectionDeadEnd__items-row" key={`collection-row--${index}`}>
                {map(row, option => (
                  <div
                    className="CollectionDeadEnd__list-item"
                    key={`collection--${get(option, 'label')}`}
                  >
                    <CarouselCard
                      heading={get(option, 'label')}
                      linkUrl={get(option, 'linkUrl')}
                      image={get(option, 'image')}
                      onClick={() =>
                        trackNavigation(
                          get(option, 'linkUrl'),
                          get(option, 'label'),
                          currentCollection
                        )
                      }
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </Container>
      <style jsx>
        {`
          .CollectionDeadEnd {
            padding: ${Theme.get('spacing') * 5}px 0;
            background-color: ${Theme.get('colors-grey')};
          }

          .CollectionDeadEnd__wrapper-stacked {
            display: flex;
            flex-direction: column;
            justify-content: center;
            padding-right: ${Theme.get('spacing')}px;
          }

          .CollectionDeadEnd__text {
            margin: ${Theme.get('spacing') * 3}px 0;
          }

          .CollectionDeadEnd__items-stacked {
            display: inline-block;
            flex: 1;
          }

          .CollectionDeadEnd__items-row {
            display: flex;
          }

          .CollectionDeadEnd__list-item {
            flex: 1;
            min-width: 0;
            display: inline-block;
            padding: ${Theme.get('spacing') * 1.5}px;
          }

          .CollectionDeadEnd__items-row .CollectionDeadEnd__list-item:first-child {
            padding-left: 0;
          }

          .CollectionDeadEnd__items-row .CollectionDeadEnd__list-item:last-child {
            padding-right: 0;
          }

          @media ${mediumUp} {
            .CollectionDeadEnd__wrapper {
              padding: 0 ${Theme.get('spacing') * 2}px;
            }

            .CollectionDeadEnd__items-stacked {
              flex: 1 1 80%;
            }

            .CollectionDeadEnd__wrapper-two-column {
              flex-direction: row;
            }

            .CollectionDeadEnd__wrapper-two-column .CollectionDeadEnd__text {
              width: 100%;
              margin: ${Theme.get('spacing') * 10}px ${Theme.get('spacing') * 8}px 0 0;
            }

            .CollectionDeadEnd__text {
              margin: ${Theme.get('spacing') * 6}px 0;
            }
          }

          @media ${largeUp} {
            .CollectionDeadEnd__wrapper {
              flex-direction: row;
              padding: 0;
            }

            .CollectionDeadEnd__list-item {
              max-width: 360px;
            }

            .CollectionDeadEnd__wrapper-two-column .CollectionDeadEnd__text {
              width: 22%;
              display: initial;
            }

            .CollectionDeadEnd__text {
              margin: ${Theme.get('spacing') * 10}px ${Theme.get('spacing') * 8}px
                ${Theme.get('spacing') * 2}px 0;
            }

            .CollectionDeadEnd__text-carousel {
              padding-left: ${Theme.get('spacing') * 8}px;
            }

            .CollectionDeadEnd__text-stacked {
              width: 22%;
            }
          }
        `}
      </style>
      <style jsx global>{`
        .CollectionDeadEnd__list-item .CarouselCard,
        .CollectionDeadEnd__items-carousel .HomepageV2Carousel .CarouselCard {
          box-shadow: none;
        }

        .HomepageV2Carousel__slide-container
          .CarouselCard
          .CarouselCard__copy
          .typography--tertiary-heading,
        .CollectionDeadEnd__list-item
          .CarouselCard
          .CarouselCard__copy
          .typography--tertiary-heading {
          font-size: ${Theme.get('font-size-description-px')};
        }

        @media ${mediumUp} {
          .HomepageV2Carousel__slide-container
            .CarouselCard
            .CarouselCard__copy
            .typography--tertiary-heading {
            font-size: ${Theme.get('font-size-px')};
          }
        }
      `}</style>
    </div>
  );
};

CollectionDeadEnd.propTypes = {
  filteredSecondareNavigationOptions: PropTypes.arrayOf(
    PropTypes.shape({
      linkUrl: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      image: PropTypes.shape({
        src: PropTypes.string.isRequired,
        altText: PropTypes.string.isRequired,
      }),
    })
  ).isRequired,
  trackNavigation: PropTypes.func.isRequired,
  is3Column: PropTypes.bool.isRequired,
  exploreMoreTitle: PropTypes.string.isRequired,
  currentCollection: PropTypes.shape({}),
};

CollectionDeadEnd.defaultProps = {
  currentCollection: {},
};

export default CollectionDeadEnd;
